import i18n from '@/i18n';

/**
 * DELETE que não apagou nada precisa DOER.
 *
 * O PostgREST responde 204 tanto para "apaguei a linha que você pediu" quanto
 * para "não achei linha nenhuma que você tivesse permissão de apagar". O
 * supabase-js reflete isso: `error` vem `null` nos dois casos. Como toda rota
 * do CRM é gateada por `<recurso>.view` e toda policy de escrita exige
 * `<recurso>.manage`, qualquer papel que tenha só a leitura ENTRA na tela, vê
 * o botão de remover, clica — e recebe um toast verde.
 *
 * Medido em produção (Arena 015, 25/08/2026): uma coordenadora tentou tirar
 * uma aluna da turma três vezes. A aluna nunca saiu; o CRM disse que sim nas
 * três. `coordinator` tem `lessons.view` e não tem `lessons.manage`.
 *
 * A assimetria é o que torna isto traiçoeiro: um INSERT barrado pela mesma RLS
 * devolve `42501` e a tela mostra erro. Só o DELETE mente, porque "zero linhas"
 * é um resultado legítimo do comando.
 *
 * Papéis afetados hoje, por área que abrem sem poder escrever:
 *   network_admin ....... 12 áreas (agenda, quadras, aulas, PDV, produtos, …)
 *   coordinator ......... 8
 *   crm_operator ........ 8
 *   finance ............. 8
 *   receptionist ........ 7
 *
 * USO — o `{ count: 'exact' }` não é opcional:
 *
 *   const res = await supabase
 *     .from('club_lesson_students')
 *     .delete({ count: 'exact' })
 *     .eq('id', enrollmentId);
 *   assertDeleted(res, 'lessons.unenroll');
 *
 * Por que `count` e não `.select()`: o `.select()` no delete exige que a policy
 * de SELECT também alcance a linha. Onde DELETE e SELECT divergem, uma remoção
 * bem-sucedida voltaria vazia e nós acusaríamos falha onde não houve — trocar
 * um falso positivo por um falso negativo não é conserto. O `count=exact` vem
 * do Content-Range, que o PostgREST preenche a partir do próprio DELETE.
 */
export class NothingDeletedError extends Error {
  /** Identificador do call site — vai para a tag do Sentry. */
  readonly context: string;

  constructor(context: string) {
    super(i18n.t('toasts.deleteNoRows'));
    this.name = 'NothingDeletedError';
    this.context = context;
  }
}

export interface DeleteResult {
  error: { message: string } | null;
  count: number | null;
}

export interface AssertDeletedOptions {
  /**
   * Para o DELETE cujo alvo pode legitimamente não existir — limpar as
   * notificações já lidas, remover um override de permissão que nunca foi
   * criado, desligar um esporte que não estava ligado. Nesses, zero linhas é a
   * resposta certa, não uma permissão faltando.
   *
   * NÃO use em delete por `id`: ali zero linhas significa ou RLS barrando, ou
   * uma tela operando sobre dado que já sumiu. As duas merecem erro.
   */
  allowEmpty?: boolean;
}

/**
 * Converte o silêncio do PostgREST em erro. Lança o erro original quando existe;
 * lança `NothingDeletedError` quando o comando não alcançou nenhuma linha.
 */
export function assertDeleted(
  res: DeleteResult,
  context: string,
  opts: AssertDeletedOptions = {},
): void {
  if (res.error) throw res.error;
  if (opts.allowEmpty) return;
  // `null` = o caller esqueceu o `{ count: 'exact' }`. Deixar passar seria
  // reabrir o buraco sem ninguém perceber.
  if (res.count === null || res.count === 0) throw new NothingDeletedError(context);
}

/**
 * O mesmo silêncio, no UPDATE.
 *
 * Quando o encerramento de matrícula (`club_lesson_students.left_at`) trocou o
 * DELETE por UPDATE, o buraco veio junto: `UPDATE` barrado por RLS também
 * afeta zero linhas e também devolve 204 sem erro. E o risco era pior que o
 * original — até 20260825160000 NÃO EXISTIA policy de UPDATE para staff nessa
 * tabela (só para dono do clube e admin), então o encerramento seria no-op
 * silencioso para manager, coordinator e professor.
 *
 * Ou seja: sem esta guarda, a correção do incidente da Arena 015 recriaria o
 * incidente da Arena 015 — e testar como dono do clube não revelaria.
 */
export class NothingUpdatedError extends Error {
  readonly context: string;

  constructor(context: string) {
    super(i18n.t('toasts.updateNoRows'));
    this.name = 'NothingUpdatedError';
    this.context = context;
  }
}

/** Idêntico ao `assertDeleted`, para quem escreve em vez de apagar. */
export function assertUpdated(
  res: DeleteResult,
  context: string,
  opts: AssertDeletedOptions = {},
): void {
  if (res.error) throw res.error;
  if (opts.allowEmpty) return;
  if (res.count === null || res.count === 0) throw new NothingUpdatedError(context);
}
